import { useState } from 'react'
import type { DraftState, WizardQuestion, WizardStep } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { TargetTaxonomyKey } from '../../../Generated/TargetTaxonomy.ts'
import type { Taxas } from '../../../Model/ITaxa.ts'
import { getProcessingTypeLabel, isProcessingTypeTaxaCombinationValid } from '../../../Util/processingType.ts'
import { taxaOptions } from '../HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import { taxonLabel } from './wizardLabels.ts'
import { useAnswer } from './useAnswer.ts'
import { ARCHIVE_NEEDS_SHEET, questionSeverity, questionTarget } from '../../../Services/OrderWizard/issues.ts'
import { cardQuestions } from '../../../Services/OrderWizard/questions.ts'
import { answerClass, QuestionCardFrame } from './QuestionCardFrame.tsx'
import type { CardProps } from './QuestionCardFrame.tsx'
import { SheetPicker } from './SheetPicker.tsx'
import { PrimersMissingCard } from './PrimersMissingCard.tsx'

const ProcessingTypeCard = ({ draft, question, primary }: CardProps<'processing-type-conflict'>) => {
  const answerWith = useAnswer(draft.id)
  const [taxon, setTaxon] = useState<TargetTaxonomyKey>(question.allowedTaxa[0] ?? 'OTHER')
  const answer = (choice: 'metabarcoding' | 'change-taxon') =>
    answerWith(
      choice === 'metabarcoding' ? { kind: 'processing-type-conflict', choice } : { kind: 'processing-type-conflict', choice, taxon }
    )
  const preferred = getProcessingTypeLabel(question.preferred)
  return (
    <QuestionCardFrame title={`${preferred} or ${taxonLabel(question.detectedTaxon)}?`} tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">
        Your organisation processes as {preferred} by default, but the files point at {taxonLabel(question.detectedTaxon)}, which{' '}
        {preferred} does not cover. The order is created once you choose.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={answerClass(primary)}
          data-testid="wizard-answer-metabarcoding"
          onClick={() => answer('metabarcoding')}
        >
          Keep {taxonLabel(question.detectedTaxon)}, process as metabarcoding
        </button>
        {question.allowedTaxa.length > 0 && (
          <span className="flex items-center gap-2">
            <span className="text-sm text-slate-600">or keep {preferred} with</span>
            <select
              className="select select-bordered select-sm"
              value={taxon}
              onChange={(event) => setTaxon(event.target.value as TargetTaxonomyKey)}
            >
              {question.allowedTaxa.map((option) => (
                <option key={option} value={option}>
                  {taxonLabel(option)}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-sm" onClick={() => answer('change-taxon')}>
              Use this
            </button>
          </span>
        )}
      </div>
    </QuestionCardFrame>
  )
}

const SheetKeyCard = ({ draft, question, primary }: CardProps<'sheet-key-column'>) => {
  const answer = useAnswer(draft.id)
  const [role, setRole] = useState<'fileForward' | 'sampleId' | 'sampleName'>('sampleName')
  const [column, setColumn] = useState(draft.sheet?.headers[0] ?? '')
  return (
    <QuestionCardFrame title="Which sheet column identifies the samples?" tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">{question.reason}. Pick the column that names the files or the samples.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="select select-bordered select-sm"
          value={column}
          onChange={(event) => setColumn(event.target.value)}
          data-testid="wizard-sheet-key-column"
        >
          {(draft.sheet?.headers ?? []).map((header) => (
            <option key={header} value={header}>
              {header}
            </option>
          ))}
        </select>
        <span className="text-sm text-slate-600">holds</span>
        <select className="select select-bordered select-sm" value={role} onChange={(event) => setRole(event.target.value as typeof role)}>
          <option value="sampleName">customer sample names</option>
          <option value="sampleId">sample ids (as in the file names)</option>
          <option value="fileForward">file names</option>
        </select>
        <button
          type="button"
          className={answerClass(primary)}
          disabled={!column}
          onClick={() => answer({ kind: 'sheet-key-column', mapping: { [role]: column } })}
        >
          Match with this column
        </button>
      </div>
    </QuestionCardFrame>
  )
}

const ControlsNoneFoundCard = ({ draft, question, primary }: CardProps<'controls-none-found'>) => {
  const answerWith = useAnswer(draft.id)
  const answer = (choice: 'none' | 'mark') => answerWith({ kind: 'controls-none-found', choice })
  return (
    <QuestionCardFrame title="We found no negative controls. Are there any?" tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">No file name looks like a negative control (NC, NTC, blank, H2O, …).</p>
      <div className="flex gap-2">
        <button type="button" className={answerClass(primary)} onClick={() => answer('mark')}>
          Let me mark them
        </button>
        <button type="button" className="btn btn-sm" data-testid="wizard-controls-none" onClick={() => answer('none')}>
          There are none
        </button>
      </div>
    </QuestionCardFrame>
  )
}

const ControlsTooManyCard = ({ draft, question, primary }: CardProps<'controls-too-many'>) => {
  const answerWith = useAnswer(draft.id)
  const answer = (choice: 'accept' | 'mark') => answerWith({ kind: 'controls-too-many', choice })
  return (
    <QuestionCardFrame
      title={`${question.flagged} of ${question.total} items look like negative controls`}
      tone={questionSeverity(question)}
    >
      <p className="mb-2 text-sm text-slate-600">That is unusually many, so nothing was labelled yet.</p>
      <div className="flex gap-2">
        <button type="button" className={answerClass(primary)} onClick={() => answer('accept')}>
          Label them as controls
        </button>
        <button type="button" className="btn btn-sm" onClick={() => answer('mark')}>
          Let me mark them
        </button>
      </div>
    </QuestionCardFrame>
  )
}

const FormatMultiplexCard = ({ draft, question, primary }: CardProps<'format-multiplex'>) => {
  const answerWith = useAnswer(draft.id)
  const answer = (choice: 'single' | 'combined') => answerWith({ kind: 'format-multiplex', choice })
  return (
    <QuestionCardFrame title="One sample per file pair, or several?" tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">
        It is unclear whether each R1/R2 pair holds one sample or several inline-tagged samples ({question.reason}). The order is created
        once you choose.
      </p>
      <div className="flex gap-2">
        <button type="button" className={answerClass(primary)} onClick={() => answer('single')}>
          One sample per pair
        </button>
        <button type="button" className="btn btn-sm" data-testid="wizard-answer-combined" onClick={() => answer('combined')}>
          Several samples per pair
        </button>
      </div>
    </QuestionCardFrame>
  )
}

const ArchiveNeedsSheetCard = ({ draft, question, primary }: CardProps<'archive-needs-sheet'>) => (
  <QuestionCardFrame title={ARCHIVE_NEEDS_SHEET} tone={questionSeverity(question)}>
    <p className="mb-2 text-sm text-slate-600">
      {question.archives === 1 ? 'The file pair holds' : `The ${question.archives} file pairs hold`} several tagged samples, and the
      pipeline separates them by each sample&apos;s exact primers. The sheet needs one row per sample: its name and its exact primers (tag
      plus primer) or its tags; with more than one archive, also the file each sample is in. The order cannot be submitted without it.
    </p>
    <SheetPicker draft={draft} testId="wizard-archive-sheet" className={answerClass(primary)}>
      Add the sample sheet
    </SheetPicker>
  </QuestionCardFrame>
)

const ReadLayoutCard = ({ draft, question, primary }: CardProps<'read-layout'>) => {
  const answerWith = useAnswer(draft.id)
  const missing = question.mate === 1 ? 'R2' : 'R1'
  const answer = (choice: { choice: 'merged'; trimmed: boolean } | { choice: 'add-mates' }) =>
    answerWith({ kind: 'read-layout', ...choice })
  const raw = question.rawLength !== undefined
  const merged = (trimmed: boolean, label: string, lead: boolean) => (
    <button
      key={label}
      type="button"
      className={answerClass(primary && lead)}
      data-testid="wizard-answer-merged"
      onClick={() => answer({ choice: 'merged', trimmed })}
    >
      {label}
    </button>
  )
  const mergedButtons =
    question.trimming === 'UNKNOWN'
      ? [merged(false, 'Merged reads, primers on', !raw), merged(true, 'Merged reads, primers removed', false)]
      : [merged(question.trimming === 'TRIMMED', 'These are already merged reads', !raw)]
  const mates = (
    <button
      key="mates"
      type="button"
      className={answerClass(primary && raw)}
      data-testid="wizard-answer-add-mates"
      onClick={() => answer({ choice: 'add-mates' })}
    >
      The {missing} files are missing
    </button>
  )
  return (
    <QuestionCardFrame title={`Are the ${missing} files missing, or are these merged reads?`} tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">
        {question.files === 1 ? 'The file has' : `The ${question.files} files have`} no mate file. Missing {missing} files wait here and
        pair up when you drop them; merged reads become one sample per file. The order is created once you choose.
      </p>
      <p className="mb-2 text-sm text-slate-800" data-testid="wizard-read-layout-hint">
        {raw
          ? `These look like raw R${question.mate} reads (all ${question.rawLength} bp) — add their ${missing} files.`
          : 'Their read lengths vary, as merged reads’ do.'}
      </p>
      <div className="flex flex-wrap gap-2">{raw ? [mates, ...mergedButtons] : [...mergedButtons, mates]}</div>
    </QuestionCardFrame>
  )
}

const PrimerTrimmingCard = ({ draft, question }: CardProps<'primer-trimming'>) => {
  const answerWith = useAnswer(draft.id)
  const answer = (trimmed: boolean) => answerWith({ kind: 'primer-trimming', trimmed })
  return (
    <QuestionCardFrame title="Are the primers still on the reads?" tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">
        {question.lean === 'on'
          ? 'A registered primer pair nearly matches the start of most files: the primers are probably on, in a variant the registry does not know.'
          : 'No registered primer pair opens most of the reads: they may be trimmed, or carry primers the registry does not know.'}{' '}
        The order is created once you choose.
      </p>
      {/* neither answer is marked unless the reads lean to it, and then in words, not colour */}
      <div className="flex gap-2">
        <button type="button" className={answerClass(false)} data-testid="wizard-answer-untrimmed" onClick={() => answer(false)}>
          Primers on the reads{question.lean === 'on' && ' (likely)'}
        </button>
        <button type="button" className={answerClass(false)} data-testid="wizard-answer-trimmed" onClick={() => answer(true)}>
          Primers removed
        </button>
      </div>
    </QuestionCardFrame>
  )
}

const unavailableFor = (draft: DraftState, taxon: Taxas): string | undefined => {
  const processingType = draft.settings?.processingType.value
  return processingType && !isProcessingTypeTaxaCombinationValid(processingType, taxon)
    ? `${getProcessingTypeLabel(processingType)} is not available for this target; change the processing type first`
    : undefined
}

const TargetTaxonCard = ({ draft, question, primary }: CardProps<'target-taxon'>) => {
  const answerWith = useAnswer(draft.id)
  const suggested = question.suggestions.filter((taxon) => !unavailableFor(draft, taxon))
  const [taxon, setTaxon] = useState<TargetTaxonomyKey | ''>('')
  const answer = (value: TargetTaxonomyKey) => answerWith({ kind: 'target-taxon', taxon: value })
  return (
    <QuestionCardFrame title="Which taxon does this order target?" tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">
        The files do not tell ({question.reason}). {suggested.length > 0 && 'The primer registry names these for the primers seen. '}The
        order is created once you choose.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {suggested.map((option, index) => (
          <button
            key={option}
            type="button"
            className={answerClass(primary && index === 0)}
            data-testid="wizard-target-suggestion"
            onClick={() => answer(option)}
          >
            {taxonLabel(option)}
          </button>
        ))}
        <select
          className="select select-bordered select-sm"
          aria-label="Target taxon"
          data-testid="wizard-target-select"
          value={taxon}
          onChange={(event) => setTaxon(event.target.value as TargetTaxonomyKey)}
        >
          <option value="" disabled>
            {suggested.length > 0 ? 'Another taxon…' : 'Choose a taxon…'}
          </option>
          {taxaOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={!!unavailableFor(draft, option.value)}
              title={unavailableFor(draft, option.value)}
            >
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={answerClass(primary && suggested.length === 0)}
          disabled={!taxon}
          data-testid="wizard-target-use"
          onClick={() => taxon && answer(taxon)}
        >
          Use this
        </button>
      </div>
    </QuestionCardFrame>
  )
}

const TargetConflictCard = ({ draft, question, primary }: CardProps<'target-conflict'>) => {
  const answerWith = useAnswer(draft.id)
  const answer = (choice: { choice: 'sheet'; taxon: TargetTaxonomyKey } | { choice: 'keep' }) =>
    answerWith({ kind: 'target-conflict', ...choice })
  const current = taxonLabel(question.current)
  const sheet = question.sheet.amplicons.map(taxonLabel).join(' / ')
  const lead = question.from === 'reads' ? `The reads look like ${current}` : `You chose ${current}`
  return (
    <QuestionCardFrame title={`${lead}, the sheet’s primers are ${sheet} — which is the target?`} tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">
        The sheet&apos;s primers ({question.sheet.names.join('; ') || 'registered pair'}) are registered for {sheet}, and submit checks the
        primers against the target. Keeping {current} leaves the sheet&apos;s primer columns out.
      </p>
      <div className="flex flex-wrap gap-2">
        {question.sheet.amplicons.map((taxon, index) => (
          <button
            key={taxon}
            type="button"
            className={answerClass(primary && index === 0)}
            disabled={!!unavailableFor(draft, taxon)}
            title={unavailableFor(draft, taxon)}
            data-testid="wizard-answer-sheet-target"
            onClick={() => answer({ choice: 'sheet', taxon })}
          >
            {taxonLabel(taxon)}, as the sheet&apos;s primers
          </button>
        ))}
        <button type="button" className="btn btn-sm" data-testid="wizard-answer-keep-target" onClick={() => answer({ choice: 'keep' })}>
          Keep {current}, without the sheet&apos;s primers
        </button>
      </div>
    </QuestionCardFrame>
  )
}

const TargetReadsConflictCard = ({ draft, question, primary }: CardProps<'target-reads-conflict'>) => {
  const answerWith = useAnswer(draft.id)
  const current = taxonLabel(question.current)
  const reads = question.reads.amplicons.map(taxonLabel).join(' / ')
  return (
    <QuestionCardFrame
      title={`The target is ${current}, the R2 reads’ primers are ${reads} — which is the target?`}
      tone={questionSeverity(question)}
    >
      <p className="mb-2 text-sm text-slate-600">
        With their R2 files the reads show the pair {question.reads.names.join('; ') || 'registered pair'}, registered for {reads}, and
        submit checks the primers against the target.
      </p>
      <div className="flex flex-wrap gap-2">
        {question.reads.amplicons.map((taxon, index) => (
          <button
            key={taxon}
            type="button"
            className={answerClass(primary && index === 0)}
            disabled={!!unavailableFor(draft, taxon)}
            title={unavailableFor(draft, taxon)}
            data-testid="wizard-answer-reads-target"
            onClick={() => answerWith({ kind: 'target-reads-conflict', choice: 'reads', taxon })}
          >
            {taxonLabel(taxon)}, as the reads&apos; primers
          </button>
        ))}
        <button
          type="button"
          className="btn btn-sm"
          data-testid="wizard-answer-keep-reads-target"
          onClick={() => answerWith({ kind: 'target-reads-conflict', choice: 'keep' })}
        >
          Keep {current}
        </button>
      </div>
    </QuestionCardFrame>
  )
}

/**
 * The draft's open question cards, or only those of one step; the first leads with
 * a primary button. The pools question's card is the step's own, above its board.
 */
export const WizardQuestions = ({ draft, step }: { draft: DraftState; step?: WizardStep }) => {
  const questions = cardQuestions(draft, step)
  if (questions.length === 0) {
    return null
  }
  const leading = questions[0]?.id
  return (
    <div className="space-y-2" data-testid="wizard-questions">
      {questions.map((question) => (
        <div key={question.id} id={questionTarget(question.id)}>
          <QuestionCard draft={draft} question={question} primary={question.id === leading} />
        </div>
      ))}
    </div>
  )
}

const QuestionCard = ({ draft, question, primary }: { draft: DraftState; question: WizardQuestion; primary: boolean }) => {
  switch (question.kind) {
    case 'processing-type-conflict':
      return <ProcessingTypeCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'sheet-key-column':
      /* keyed on the sheet: a column picked for one sheet is no choice for the next */
      return <SheetKeyCard key={`${question.id}:${draft.sheet?.fileKey}`} draft={draft} question={question} primary={primary} />
    case 'controls-none-found':
      return <ControlsNoneFoundCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'controls-too-many':
      return <ControlsTooManyCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'pools':
      return null
    case 'primers-missing':
      return <PrimersMissingCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'format-multiplex':
      return <FormatMultiplexCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'archive-needs-sheet':
      return <ArchiveNeedsSheetCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'read-layout':
      return <ReadLayoutCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'primer-trimming':
      return <PrimerTrimmingCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'target-taxon':
      return <TargetTaxonCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'target-conflict':
      return <TargetConflictCard key={question.id} draft={draft} question={question} primary={primary} />
    case 'target-reads-conflict':
      return <TargetReadsConflictCard key={question.id} draft={draft} question={question} primary={primary} />
  }
}
